import React, { useEffect, useMemo } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getQuote } from "../../../features/product";
import { getISOFormattedDate } from "../../../utils/commonHelper";
import {
  convertToISOWithPlus365,
  getYearDifferenceFromToday,
} from "../../../utils/gigHelpers";
import SelectDropdown from "../../SelectDropdown";
import { Button } from "react-bootstrap";
import { Loader } from "../../common/loader";
import { getCountry } from "../../../features/country";

const prefixOptions = [
  { value: "SC01", label: "MR" },
  { value: "SC02", label: "MS" },
];

const genderOptions = [
  { value: "GC01", label: "Female" },
  { value: "GC02", label: "Male" },
];

const nationalityOptions = [
  { value: "OMN", label: "Oman" },
  { value: "IND", label: "India" },
];

const professionOptions = [
  { value: "PROF0128", label: "Others" },
  { value: "PROF0001", label: "Engineer" },
];

const residenceOptions = [
  { value: "OMN11", label: "MUSCAT" },
  { value: "OMN12", label: "SALALAH" },
];

const PolicyHolderSchema = Yup.object().shape({
  person: Yup.object().shape({
    prefixTitle: Yup.object().required("Required"),
    surName: Yup.string().required("Required"),
    givenName: Yup.string().required("Required"),
    gender: Yup.object().required("Required"),
    nationality: Yup.object().required("Required"),
    // professionName: Yup.object().required("Required"),
    residenceLocation: Yup.object().required("Required"),
  }),
  isPolicyHolderDriver: Yup.boolean(),
  firstDrivingLicenseIssueCountry: Yup.object().required("Required"),
  isDrivingLicenseTransferrable: Yup.boolean(),
  contactMethods: Yup.object().shape({
    phoneContacts: Yup.array().of(
      Yup.object().shape({
        phoneNumber: Yup.string()
          .matches(/^\d{6}$/, "Phone number must be exactly 6 digits") // if we put above 6 api throw error {"message": "Mobile Number should be 6 characters."}
          .required("Required"),
      })
    ),
    emailContact: Yup.array().of(
      Yup.object().shape({
        url: Yup.string().email("Invalid email").required("Required"),
      })
    ),
  }),
  documents: Yup.object().shape({
    id: Yup.object().shape({
      documentContent: Yup.string().required("ID document is required"),
      documentName: Yup.string().required("ID document name is required"), // Add document name validation
    }),
    drivingLicense: Yup.object().shape({
      documentContent: Yup.string().required("Driving License is required"),
      documentName: Yup.string().required("Driving License name is required"), // Add document name validation
    }),
    civilId: Yup.object().shape({
      documentContent: Yup.string().required("Civil ID is required"),
      documentName: Yup.string().required("Civil ID name is required"), // Add document name validation
    }),
  }),
  // New fields for document numbers and expiry dates
  idDocumentNumber: Yup.string().required("ID Document Number is required"),
  idDocumentExpiryDate: Yup.date()
    .required("ID Document Expiry Date is required")
    .nullable(),
  drivingLicenseNumber: Yup.string().required(
    "Driving License Number is required"
  ),
  drivingLicenseExpiryDate: Yup.date()
    .required("Driving License Expiry Date is required")
    .nullable(),
  civilIdNumber: Yup.string().required("Civil ID Number is required"),
  civilIdExpiryDate: Yup.date()
    .required("Civil ID Expiry Date is required")
    .nullable(),
});

const GigMotorPersonalDetailForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const quoteLoader = useSelector((state) => state.product.quoteLoader);
  const planData = useMemo(
    () => location?.state?.data || {},
    [location?.state]
  );
  const { countryLoader, data: countryData } = useSelector(
    (state) => state.country
  );

  useEffect(() => {
    dispatch(getCountry());
  }, []);

  const handleFileUpload = async (e, setFieldValue, path) => {
    const file = e.currentTarget.files[0];

    if (file) {
      const reader = new FileReader();
      // Get current epoch time in milliseconds
      const epochMilliseconds = Date.now();

      // Split filename and extension
      const nameParts = file.name.split(".");
      const ext = nameParts.pop();
      const baseName = nameParts.join(".");

      // Append epoch to filename
      const updatedFileName = `${baseName}_${epochMilliseconds}.${ext}`;

      reader.onloadend = () => {
        const base64String = reader.result.split(",")[1];
        setFieldValue(`${path}.documentName`, updatedFileName);
        setFieldValue(`${path}.documentContent`, base64String);
      };

      reader.readAsDataURL(file);
    }
  };

  const renameLabelToValue = (obj) => {
    if (!obj || typeof obj !== "object") return null;
    const { code, label } = obj;
    return { code, value: label };
  };

  if (quoteLoader || countryLoader || !planData || !planData?.extracted_data) {
    return <Loader type="logo-gif" />;
  }

  return (
    <div className="container mt-4 mb-5">
      <div className="policy-formBox why-us-form p-4">
        <h3 className="pb-3 fw-bold">Policy Holder Form</h3>
        <Formik
          initialValues={{
            person: {
              prefixTitle: null,
              surName: "",
              givenName: "",
              middleName: "",
              gender: null,
              nationality: null,
              professionName: {
                code: "PROF0128",
                label: "Others",
              },
              residenceLocation: null,
            },
            isPolicyHolderDriver: true,
            policyHolderDrivingExperience: getYearDifferenceFromToday(
              planData?.extracted_data?.drivingLicenseIssueDate
            ),
            firstDrivingLicenseIssueCountry: null,
            isDrivingLicenseTransferrable: false,
            contactMethods: {
              postMailContact: [
                {
                  addressType: "-",
                  houseNr: "0",
                  buildingNr: "0",
                  addressLine1: {
                    code: "SAREA00676",
                    value: "Muscat",
                  },
                  city: {
                    applicationCode: "",
                    code: "OMN11",
                    value: "MUSCAT",
                  },
                  country: {
                    applicationCode: "",
                    code: "OMN",
                    value: "Oman",
                  },
                },
              ],
              phoneContacts: [
                {
                  phoneType: "MOBILE",
                  prefixCode: "+968",
                  operatorCode: "98",
                  phoneNumber: "",
                  internationalFullNumber: "",
                },
              ],
              emailContact: [
                {
                  type: "PERSONAL",
                  url: "",
                },
              ],
            },
            documents: {
              id: { documentName: "", documentContent: "" },
              drivingLicense: { documentName: "", documentContent: "" },
              civilId: { documentName: "", documentContent: "" },
            },
            idDocumentNumber: "",
            idDocumentExpiryDate: "",
            drivingLicenseNumber: "",
            drivingLicenseExpiryDate: "",
            civilIdNumber: "",
            civilIdExpiryDate: "",
          }}
          validationSchema={PolicyHolderSchema}
          onSubmit={(values) => {
            const {
              documents,
              idDocumentNumber,
              idDocumentExpiryDate,
              drivingLicenseNumber,
              drivingLicenseExpiryDate,
              civilIdNumber,
              civilIdExpiryDate,
              ...gig_policy_holder
            } = values;
            const { extracted_data } = planData;

            const person = {
              ...gig_policy_holder.person,
              birthDate: extracted_data?.dateofBirth,
              prefixTitle: renameLabelToValue(
                gig_policy_holder.person.prefixTitle
              ),
              gender: renameLabelToValue(gig_policy_holder.person.gender),
              nationality: renameLabelToValue(
                gig_policy_holder.person.nationality
              ),
              professionName: renameLabelToValue(
                gig_policy_holder.person.professionName
              ),
              residenceLocation: renameLabelToValue(
                gig_policy_holder.person.residenceLocation
              ),
            };

            const selectedPlan = {
              product: {
                code: planData?.plan_code,
                value: planData?.plan_name,
              },
              uwApprovalStatus: "Y",
              covers: planData?.included_covers
                .concat(planData?.optional_covers)
                .filter((item) => item?.isSelected === true),
            };

            const gig_documents_array = [
              {
                docType: {
                  code: "DT03",
                  value: "ID",
                },
                docId: idDocumentNumber, // Use the new field
                docExpiryDate: idDocumentExpiryDate, // Format date
                isVerified: false,
              },
              {
                docType: {
                  code: "DT01",
                  value: "Civil ID", // Corrected type for Civil ID if needed
                },
                docId: civilIdNumber, // Use the new field
                docExpiryDate: civilIdExpiryDate, // Format date
                isVerified: false,
              },
              {
                docType: {
                  code: "DT02",
                  value: "Driving License",
                },
                docId: drivingLicenseNumber, // Use the new field
                docExpiryDate: drivingLicenseExpiryDate, // Format date
                isVerified: false,
              },
            ];

            const getDates = convertToISOWithPlus365(
              extracted_data?.insuranceEndDate
            );
            // console.log({ getDates, extracted_data });

            dispatch(
              getQuote({
                ...extracted_data,
                gig_policy_holder: {
                  ...gig_policy_holder,
                  person,
                  firstDrivingLicenseIssueCountry: renameLabelToValue(
                    gig_policy_holder.firstDrivingLicenseIssueCountry
                  ),
                  documents: gig_documents_array,
                },
                gig_documents_array,
                selectedPlan,
                planData,
                premium_value: planData?.premium_value,
                premium_currency: planData?.premium_currency,
                insurance_company_product_mapping_id:
                  planData?.insurance_company_product_mapping_id,
                company_id: planData?.company_id,
                gigPlateNumber: `${extracted_data?.plateCode}-${extracted_data?.plateNumber}`,
                licenceDocumentName: documents?.drivingLicense?.documentName,
                licenceDocumentContent:
                  documents?.drivingLicense?.documentContent,
                idDocumentName: documents?.id?.documentName,
                idDocumentContent: documents?.id?.documentContent,
                civilIdDocumentName: documents?.civilId?.documentName,
                civilIdDocumentContent: documents?.civilId?.documentContent,
                vehicleChassisNumber: extracted_data?.vehicleChassisNumber,
                // gig_effective_date: getDates?.originalISO,
                // gig_expiry_date: getDates?.plus365ISO
                gig_effective_date: getISOFormattedDate(1),
                gig_expiry_date: getISOFormattedDate(365),
                vehicle_engine_no_rop: extracted_data?.vehicle_engine_no,
              })
            ).then((res) => {
              // console.log(res);
              navigate("/checkout", { state: { existingData: res.payload } });
            });
          }}
        >
          {({ values, setFieldValue, errors, touched }) => (
            <Form>
              <div className="row g-3">
                {/* Prefix Title */}
                <div className="col-md-6 col-lg-4 col-sm-12">
                  <label className="form-label">Prefix Title</label>
                  <SelectDropdown
                    options={prefixOptions}
                    handleOnChange={(name, value, index, label) => {
                      setFieldValue("person.prefixTitle", {
                        code: value,
                        label,
                      });
                    }}
                    className={
                      touched.person?.prefixTitle && errors.person?.prefixTitle
                        ? "is-invalid element-style"
                        : "element-style"
                    }
                  />
                  <ErrorMessage
                    name="person.prefixTitle"
                    component="div"
                    className="text-danger"
                  />
                </div>

                {/* Given Name */}
                <div className="col-md-6 col-lg-4 col-sm-12">
                  <label className="form-label">Given Name</label>
                  <Field
                    name="person.givenName"
                    className={`element-style ${
                      touched.person?.givenName && errors.person?.givenName
                        ? "is-invalid"
                        : ""
                    }`}
                  />
                  <ErrorMessage
                    name="person.givenName"
                    component="div"
                    className="text-danger"
                  />
                </div>

                {/* Surname */}
                <div className="col-md-6 col-lg-4 col-sm-12">
                  <label className="form-label">Surname</label>
                  <Field
                    name="person.surName"
                    className={`element-style ${
                      touched.person?.surName && errors.person?.surName
                        ? "is-invalid"
                        : ""
                    }`}
                  />
                  <ErrorMessage
                    name="person.surName"
                    component="div"
                    className="text-danger"
                  />
                </div>

                {/* Gender */}
                <div className="col-md-6 col-lg-4 col-sm-12">
                  <label className="form-label">Gender</label>
                  <SelectDropdown
                    options={genderOptions}
                    handleOnChange={(name, value, index, label) => {
                      setFieldValue("person.gender", { code: value, label });
                    }}
                    className={
                      touched.person?.gender && errors.person?.gender
                        ? "is-invalid"
                        : ""
                    }
                  />
                  <ErrorMessage
                    name="person.gender"
                    component="div"
                    className="text-danger"
                  />
                </div>

                {/* Nationality */}
                <div className="col-md-6 col-lg-4 col-sm-12">
                  <label className="form-label">Nationality</label>
                  <SelectDropdown
                    options={
                      countryData && countryData?.length > 0
                        ? countryData.map((item) => ({
                            value: item.short_code,
                            label: item.name,
                          }))
                        : []
                    }
                    handleOnChange={(name, value, index, label) => {
                      setFieldValue("person.nationality", {
                        code: value,
                        label,
                      });
                    }}
                    className={
                      touched.person?.nationality && errors.person?.nationality
                        ? "is-invalid"
                        : ""
                    }
                  />
                  <ErrorMessage
                    name="person.nationality"
                    component="div"
                    className="text-danger"
                  />
                </div>

                {/* Profession */}
                {/* <div className="col-md-6 col-lg-4 col-sm-12">
                  <label className="form-label">Profession</label>
                  <SelectDropdown
                    options={professionOptions}
                    handleOnChange={(name, value, index, label) => {
                      setFieldValue("person.professionName", {
                        code: value,
                        label,
                      });
                    }}
                    className={
                      touched.person?.professionName &&
                      errors.person?.professionName
                        ? "is-invalid"
                        : ""
                    }
                  />
                  <ErrorMessage
                    name="person.professionName"
                    component="div"
                    className="text-danger"
                  />
                </div> */}

                {/* Residence Location */}
                <div className="col-md-6 col-lg-4 col-sm-12">
                  <label className="form-label">Residence Location</label>
                  <SelectDropdown
                    options={residenceOptions}
                    handleOnChange={(name, value, index, label) => {
                      setFieldValue("person.residenceLocation", {
                        code: value,
                        label,
                      });
                    }}
                    className={
                      touched.person?.residenceLocation &&
                      errors.person?.residenceLocation
                        ? "is-invalid"
                        : ""
                    }
                  />
                  <ErrorMessage
                    name="person.residenceLocation"
                    component="div"
                    className="text-danger"
                  />
                </div>

                {/* First License Country */}
                <div className="col-md-6 col-lg-4 col-sm-12">
                  <label className="form-label">
                    First Driving License Country
                  </label>
                  <SelectDropdown
                    options={
                      countryData && countryData?.length > 0
                        ? countryData.map((item) => ({
                            value: item.short_code,
                            label: item.name,
                          }))
                        : []
                    }
                    handleOnChange={(name, value, index, label) => {
                      setFieldValue("firstDrivingLicenseIssueCountry", {
                        code: value,
                        label,
                      });
                    }}
                    className={
                      touched.firstDrivingLicenseIssueCountry &&
                      errors.firstDrivingLicenseIssueCountry
                        ? "is-invalid"
                        : ""
                    }
                  />
                  <ErrorMessage
                    name="firstDrivingLicenseIssueCountry"
                    component="div"
                    className="text-danger"
                  />
                </div>

                {/* Phone Number */}
                <div className="col-md-6 col-lg-4 col-sm-12">
                  <label className="form-label">Phone Number</label>
                  <Field
                    name="contactMethods.phoneContacts[0].phoneNumber"
                    className={`element-style ${
                      touched.contactMethods?.phoneContacts?.[0]?.phoneNumber &&
                      errors.contactMethods?.phoneContacts?.[0]?.phoneNumber
                        ? "is-invalid"
                        : ""
                    }`}
                  />
                  <ErrorMessage
                    name="contactMethods.phoneContacts[0].phoneNumber"
                    component="div"
                    className="text-danger"
                  />
                </div>

                {/* Email */}
                <div className="col-md-6 col-lg-4 col-sm-12">
                  <label className="form-label">Email</label>
                  <Field
                    name="contactMethods.emailContact[0].url"
                    type="email"
                    className={`element-style ${
                      touched.contactMethods?.emailContact?.[0]?.url &&
                      errors.contactMethods?.emailContact?.[0]?.url
                        ? "is-invalid"
                        : ""
                    }`}
                  />
                  <ErrorMessage
                    name="contactMethods.emailContact[0].url"
                    component="div"
                    className="text-danger"
                  />
                </div>
              </div>
              <div className="row mt-4">
                <h3 className="pb-3 fw-bold">Upload Documents</h3>
                {/* ID Document */}
                <div className="col-md-6 col-lg-4 col-sm-12">
                  <div className="form-group mb-3">
                    <label className="form-label">ID Document Number</label>
                    <Field
                      name="idDocumentNumber"
                      type="text"
                      className={`element-style ${
                        touched.idDocumentNumber && errors.idDocumentNumber
                          ? "is-invalid"
                          : ""
                      }`}
                    />
                    <ErrorMessage
                      name="idDocumentNumber"
                      component="div"
                      className="text-danger"
                    />
                  </div>
                  <div className="form-group mb-3">
                    <label className="form-label">
                      ID Document Expiry Date
                    </label>
                    <Field
                      name="idDocumentExpiryDate"
                      type="date"
                      className={`element-style ${
                        touched.idDocumentExpiryDate &&
                        errors.idDocumentExpiryDate
                          ? "is-invalid"
                          : ""
                      }`}
                    />
                    <ErrorMessage
                      name="idDocumentExpiryDate"
                      component="div"
                      className="text-danger"
                    />
                  </div>
                  <div className="form-group mb-3">
                    <label className="form-label">ID Document</label>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf"
                      className={`element-style ${
                        touched.documents?.id?.documentContent &&
                        errors.documents?.id?.documentContent
                          ? "is-invalid"
                          : ""
                      }`}
                      onChange={(e) =>
                        handleFileUpload(e, setFieldValue, "documents.id")
                      }
                    />
                    <ErrorMessage
                      name="documents.id.documentContent"
                      component="div"
                      className="text-danger"
                    />
                  </div>
                </div>

                {/* Driving License */}
                <div className="col-md-6 col-lg-4 col-sm-12">
                  <div className="form-group mb-3">
                    <label className="form-label">Driving License Number</label>
                    <Field
                      name="drivingLicenseNumber"
                      type="text"
                      className={`element-style ${
                        touched.drivingLicenseNumber &&
                        errors.drivingLicenseNumber
                          ? "is-invalid"
                          : ""
                      }`}
                    />
                    <ErrorMessage
                      name="drivingLicenseNumber"
                      component="div"
                      className="text-danger"
                    />
                  </div>
                  <div className="form-group mb-3">
                    <label className="form-label">
                      Driving License Expiry Date
                    </label>
                    <Field
                      name="drivingLicenseExpiryDate"
                      type="date"
                      className={`element-style ${
                        touched.drivingLicenseExpiryDate &&
                        errors.drivingLicenseExpiryDate
                          ? "is-invalid"
                          : ""
                      }`}
                    />
                    <ErrorMessage
                      name="drivingLicenseExpiryDate"
                      component="div"
                      className="text-danger"
                    />
                  </div>
                  <div className="form-group mb-3">
                    <label className="form-label">Driving License</label>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf"
                      className={`element-style ${
                        touched.documents?.drivingLicense?.documentContent &&
                        errors.documents?.drivingLicense?.documentContent
                          ? "is-invalid"
                          : ""
                      }`}
                      onChange={(e) =>
                        handleFileUpload(
                          e,
                          setFieldValue,
                          "documents.drivingLicense"
                        )
                      }
                    />
                    <ErrorMessage
                      name="documents.drivingLicense.documentContent"
                      component="div"
                      className="text-danger"
                    />
                  </div>
                </div>

                {/* Civil ID */}
                <div className="col-md-6 col-lg-4 col-sm-12">
                  <div className="form-group mb-3">
                    <label className="form-label">Civil ID Number</label>
                    <Field
                      name="civilIdNumber"
                      type="text"
                      className={`element-style ${
                        touched.civilIdNumber && errors.civilIdNumber
                          ? "is-invalid"
                          : ""
                      }`}
                    />
                    <ErrorMessage
                      name="civilIdNumber"
                      component="div"
                      className="text-danger"
                    />
                  </div>
                  <div className="form-group mb-3">
                    <label className="form-label">Civil ID Expiry Date</label>
                    <Field
                      name="civilIdExpiryDate"
                      type="date"
                      className={`element-style ${
                        touched.civilIdExpiryDate && errors.civilIdExpiryDate
                          ? "is-invalid"
                          : ""
                      }`}
                    />
                    <ErrorMessage
                      name="civilIdExpiryDate"
                      component="div"
                      className="text-danger"
                    />
                  </div>
                  <div className="form-group mb-3">
                    <label className="form-label">Civil ID</label>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf"
                      className={`element-style ${
                        touched.documents?.civilId?.documentContent &&
                        errors.documents?.civilId?.documentContent
                          ? "is-invalid"
                          : ""
                      }`}
                      onChange={(e) =>
                        handleFileUpload(e, setFieldValue, "documents.civilId")
                      }
                    />
                    <ErrorMessage
                      name="documents.civilId.documentContent"
                      component="div"
                      className="text-danger"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <Button type="submit" className=" mt-2 h-btn">
                  Submit
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default GigMotorPersonalDetailForm;
